import { Controller } from '@cedarjs/forms'
import { useFormContext } from '@cedarjs/forms'
import {
  Autocomplete as MuiAutocomplete,
  AutocompleteProps,
  Chip,
  TextField,
} from '@mui/material'
import theme, { wwAppColors } from 'src/theme'
import React, { useMemo } from 'react'

type CustomAutocompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
> = AutocompleteProps<T, Multiple, DisableClearable, FreeSolo>

export const Autocomplete = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(
  props: CustomAutocompleteProps<T, Multiple, DisableClearable, FreeSolo>
) => {
  const { renderInput, renderTags, ...otherProps } = props
  const [open, setOpen] = React.useState(false)

  return (
    <MuiAutocomplete
      {...otherProps}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderInput={renderInput || ((params) => <TextField {...params} />)}
      renderTags={
        renderTags ||
        ((value: T[], getTagProps) =>
          value.map((option, index) => {
            const chipProps = getTagProps({ index })
            return (
              <Chip
                {...chipProps}
                variant="soft"
                label={props.getOptionLabel?.(option) || String(option)}
                key={`chip-${
                  props.getOptionLabel?.(option) || String(option)
                }-${index}`}
                className="mb-0.5 mr-1 rounded-full"
                onDelete={(e) => {
                  setOpen(false)
                  chipProps.onDelete?.(e)
                }}
                sx={{
                  backgroundColor: wwAppColors.coolBlue[100],
                  color: theme.palette.info.dark,
                  padding: '12px 8px',
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.info.dark,
                    marginLeft: '3px',
                    marginRight: '-3px',
                    height: '18px',
                  },
                }}
              />
            )
          }))
      }
    />
  )
}

export interface AutocompleteOption {
  label: string
  value: string | number
}

interface AutocompleteFieldProps
  extends Omit<React.ComponentProps<typeof Autocomplete>, 'renderInput'> {
  name: string
  label: string
  helperText?: string
  size?: 'small' | 'medium'
  options: AutocompleteOption[]
  className?: string
  growHeight?: boolean
  required?: boolean
}

export const AutocompleteField = ({
  name,
  options,
  size = 'small',
  multiple = false,
  ...props
}: AutocompleteFieldProps & {
  stateSpecificInputProps?: {
    selected?: React.ComponentProps<typeof TextField>
    empty?: React.ComponentProps<typeof TextField>
  }
}) => {
  const {
    formState: { errors },
    control,
  } = useFormContext()

  const fieldError =
    errors[name]?.message ||
    (errors[name]?.type === 'required' && 'This field is required')

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={props.defaultValue}
      rules={{ required: props.required }}
      render={({ field: { onChange, value: formValue } }) => {
        const value = useMemo(
          () =>
            multiple
              ? options.filter(
                  (option) =>
                    Array.isArray(formValue) && formValue.includes(option.value)
                )
              : options.find((option) => option.value === formValue) ||
                formValue ||
                null,
          [options, formValue]
        )

        const hasSelectedValue = useMemo(() => {
          if (Array.isArray(value)) {
            return value.length > 0
          }
          if (typeof value === 'string') {
            // A plain string value means that the user has typed an input but not selected an existing option
            return false
          }
          return !!value
        }, [value])

        return (
          <Autocomplete
            freeSolo
            multiple={multiple}
            options={options}
            includeInputInList
            autoSelect
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            size={size}
            onChange={(_e, value: unknown) => {
              let selectedValue: string | string[] | null = null
              if (typeof value === 'string') {
                selectedValue = value
              } else if (
                value &&
                typeof value === 'object' &&
                'value' in value &&
                typeof value.value === 'string'
              ) {
                selectedValue = value.value
              } else if (Array.isArray(value)) {
                selectedValue = value.map((v) => v.value)
              }
              onChange(selectedValue)
            }}
            value={value}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={props.label}
                helperText={fieldError as string}
                error={!!fieldError}
                placeholder="Search"
                {...(hasSelectedValue
                  ? props.stateSpecificInputProps?.selected
                  : {})}
                {...(!value ? props.stateSpecificInputProps?.empty : {})}
              />
            )}
            {...props}
          />
        )
      }}
    />
  )
}

export default Autocomplete
